// ============================================================================
// Roof Designer (Stufe 1) — Engine (computeDesignerResult)
// ----------------------------------------------------------------------------
// Wandelt DesignerState deterministisch in DesignerResult um. Delegiert:
//   - Fläche (planar, m²)   → geometry-core.polygonAreaPlanar
//   - Neigungskorrektur      → geometry-core.trueAreaM2
//   - PiP-Test               → geometry-core.pointInPolygon
//   - kWp-Berechnung         → roof-core.kwpFromCount
// Pixel-basierter Grid-Fill (Stufe 1): kein GeoPoint, keine Satelliten-Daten.
// Unveränderlich: canApprove false, requiresHumanReview true.
// ============================================================================
import { polygonAreaPlanar, trueAreaM2 as trueArea, pointInPolygon } from "@energyos/geometry-core";
import { kwpFromCount } from "@energyos/roof-core";
import type { Vec2 } from "@energyos/geometry-core";
import type { DesignerState, PixelPt, PixelRect, ObstacleDraft } from "./types.js";

// ─── Exportierte Typen ───────────────────────────────────────────────────────
export type DesignerSeverity = "critical" | "warning" | "advice" | "info";

export interface DesignerFinding {
  code: string;
  severity: DesignerSeverity;
  message: string;
}

export interface DesignerResult {
  status: "ok" | "blocked";
  projectedAreaM2: number | null;
  trueAreaM2: number | null;
  usedAreaM2: number | null;
  packingDensity: number;
  moduleCount: number;
  kwp: number | null;
  missingData: string[];
  confidenceScore: number;
  blockedReason: string | null;
  findings: DesignerFinding[];
  plausibilityVerdict: string | null;
  plausibilitySummary: string | null;
  pixelPlacements: PixelRect[];
  canApprove: false;
  requiresHumanReview: true;
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────
function blocked(reason: string, missingData: string[] = [], findings: DesignerFinding[] = []): DesignerResult {
  return {
    status: "blocked",
    projectedAreaM2: null,
    trueAreaM2: null,
    usedAreaM2: null,
    packingDensity: 0,
    moduleCount: 0,
    kwp: null,
    missingData,
    confidenceScore: 0,
    blockedReason: reason,
    findings,
    plausibilityVerdict: null,
    plausibilitySummary: null,
    pixelPlacements: [],
    canApprove: false,
    requiresHumanReview: true,
  };
}

function rectOverlapsExpandedObstacle(
  modX: number, modY: number, modW: number, modH: number,
  obs: ObstacleDraft, setbackPx: number,
): boolean {
  const ox = obs.x - setbackPx;
  const oy = obs.y - setbackPx;
  const ow = obs.w + 2 * setbackPx;
  const oh = obs.h + 2 * setbackPx;
  return modX < ox + ow && modX + modW > ox && modY < oy + oh && modY + modH > oy;
}

// ─── Hauptfunktion ───────────────────────────────────────────────────────────
export function computeDesignerResult(state: DesignerState): DesignerResult {
  const findings: DesignerFinding[] = [];
  const missingData: string[] = [];

  // 1) Polygon muss geschlossen und >= 3 Punkte haben.
  if (state.polygon.length < 3 || !state.polygonClosed) {
    return blocked("Kein geschlossenes Dachpolygon. Polygon zeichnen und schliessen.");
  }

  // 2) Maßstab muss gesetzt und bestätigt sein.
  if (!state.calibration.confirmed || !state.calibration.metersPerPixel) {
    missingData.push("Maßstab nicht gesetzt oder bestaetigt");
    return blocked("Maßstab fehlt. Werkzeug Maßstab wählen, zwei Punkte klicken, Länge eingeben und Setzen.", missingData);
  }

  const mPerPx = state.calibration.metersPerPixel;

  // 3) Polygon in Metern (Shoelace braucht metrische Einheit).
  const polyM: Vec2[] = state.polygon.map((p: PixelPt) => ({ x: p.x * mPerPx, y: p.y * mPerPx }));
  const projectedAreaM2 = polygonAreaPlanar(polyM);

  // 4) Neigungskorrektur.
  if (!state.tiltConfirmed) {
    missingData.push("Dachneigung nicht bestätigt");
    findings.push({
      code: "tilt_unconfirmed",
      severity: "warning",
      message: `Neigung ${state.tiltDeg}° nicht bestätigt — wird als Annahme geführt. Checkbox Neigung bestätigt setzen.`,
    });
  }
  const trueAreaM2Val = trueArea(projectedAreaM2, state.tiltDeg);

  // 5) Modul-Abmessungen in Pixeln (orientierungsabhängig).
  const modWpx =
    (state.orientation === "portrait" ? state.moduleSpec.widthM : state.moduleSpec.heightM) / mPerPx;
  const modHpx =
    (state.orientation === "portrait" ? state.moduleSpec.heightM : state.moduleSpec.widthM) / mPerPx;
  const gapPx = state.moduleGapM / mPerPx;
  const rowGapPx = state.rowGapM / mPerPx;
  const edgePx = state.edgeSetbackM / mPerPx;
  const obsPx = state.obstacleSetbackM / mPerPx;

  if (modWpx < 2 || modHpx < 2) {
    return blocked("Module zu klein relativ zum Maßstab. Modulgrösse oder Maßstab prüfen.", missingData, findings);
  }

  // 6) Bounding Box des Pixel-Polygons.
  const xs = state.polygon.map((p) => p.x);
  const ys = state.polygon.map((p) => p.y);
  const minX = Math.min(...xs) + edgePx;
  const maxX = Math.max(...xs) - edgePx;
  const minY = Math.min(...ys) + edgePx;
  const maxY = Math.max(...ys) - edgePx;

  // 7) Grid-Fill im Pixel-Raum (PiP via geometry-core.pointInPolygon).
  const placements: PixelRect[] = [];
  const poly2d = state.polygon as unknown as Vec2[]; // PixelPt ≡ Vec2 strukturell

  for (let y = minY; y + modHpx <= maxY; y += modHpx + rowGapPx) {
    for (let x = minX; x + modWpx <= maxX; x += modWpx + gapPx) {
      const cx = x + modWpx / 2;
      const cy = y + modHpx / 2;

      if (!pointInPolygon({ x: cx, y: cy }, poly2d)) continue;

      const blocked_ = state.obstacles.some((obs) =>
        rectOverlapsExpandedObstacle(x, y, modWpx, modHpx, obs, obsPx),
      );
      if (blocked_) continue;

      placements.push({ x, y, w: modWpx, h: modHpx });
    }
  }

  // 8) KPIs.
  const moduleCount = placements.length;
  const kwp = kwpFromCount(moduleCount, state.moduleSpec.wp);
  const modWm = state.orientation === "portrait" ? state.moduleSpec.widthM : state.moduleSpec.heightM;
  const modHm = state.orientation === "portrait" ? state.moduleSpec.heightM : state.moduleSpec.widthM;
  const usedAreaM2 = moduleCount * modWm * modHm;
  const packingDensity = projectedAreaM2 > 0 ? usedAreaM2 / projectedAreaM2 : 0;

  // 9) Plausibilitäts-Befunde.
  if (moduleCount === 0) {
    findings.push({
      code: "no_modules",
      severity: "critical",
      message: "Keine Module platzierbar. Polygon, Maßstab, Modul-Abmessungen und Abstände prüfen.",
    });
  }
  if (packingDensity > 0.92) {
    findings.push({
      code: "density_high",
      severity: "warning",
      message: `Packdichte ${Math.round(packingDensity * 100)} % — Abstände und Setbacks prüfen.`,
    });
  }
  if (projectedAreaM2 < 1) {
    findings.push({
      code: "area_tiny",
      severity: "advice",
      message: "Dachfläche unter 1 m² — Polygon oder Maßstab prüfen.",
    });
  }

  const confidenceScore =
    state.calibration.confirmed && state.tiltConfirmed ? 0.85 : 0.6;

  const verdicts: DesignerSeverity[] = findings.map((f) => f.severity);
  const plausibilityVerdict: string = verdicts.includes("critical")
    ? "critical"
    : verdicts.includes("warning")
      ? "warning"
      : verdicts.includes("advice")
        ? "advice"
        : "ok";

  const plausibilitySummary = `${moduleCount} Module, ${kwp.toFixed(2)} kWp — ENTWURF, keine technische Freigabe.`;

  return {
    status: "ok",
    projectedAreaM2,
    trueAreaM2: trueAreaM2Val,
    usedAreaM2,
    packingDensity,
    moduleCount,
    kwp,
    missingData,
    confidenceScore,
    blockedReason: null,
    findings,
    plausibilityVerdict,
    plausibilitySummary,
    pixelPlacements: placements,
    canApprove: false,
    requiresHumanReview: true,
  };
}

// ─── Design-Export ───────────────────────────────────────────────────────────
export const DESIGN_DISCLAIMER =
  "ENTWURF — Dieser Plan ist ein unverbindlicher Entwurf. Keine statische, elektrische oder behördliche Freigabe. Endprüfung durch eine Fachkraft erforderlich.";

export interface DesignSummary {
  exportedAt: string; // ISO timestamp
  isDraft: boolean;   // always true
  requiresHumanReview: boolean; // always true
  // Herkunft bei Import aus einer Gebäude-Dachfläche (null = manuell gezeichnet).
  // Snapshot der Quellwerte zum Importzeitpunkt — für Nachvollziehbarkeit im
  // gespeicherten summaryJson, NICHT als Freigabe-Beleg.
  source: {
    type: "roof_surface";
    sourceRoofSurfaceId: string;
    buildingId: string;
    label: string;
    importedAreaM2: number;
    importedTiltDeg: number;
    importedAzimuthDeg: number;
    geometrySimplified: boolean;
    importedAt: string;
  } | null;
  polygon: PixelPt[];
  scale: { pixelsPerMeter: number } | null;
  obstacles: Array<{ label: string; polygon: PixelPt[] }>;
  result: {
    roofAreaM2: number | null;
    usableAreaM2: number | null;
    moduleCount: number | null;
    estimatedKwp: number | null;
    packingDensityPct: number | null;
    warnings: string[];
    missingInputs: string[];
    status: "draft" | "missing_data" | "error";
  };
  disclaimer: string;
}

export function exportDesignSummary(state: DesignerState, result: DesignerResult): DesignSummary {
  const hasPolygon = state.polygon.length >= 3 && state.polygonClosed;
  const hasScale = state.calibration.confirmed && !!state.calibration.metersPerPixel;

  const resultStatus: "draft" | "missing_data" | "error" =
    result.status === "blocked"
      ? hasPolygon
        ? "missing_data"
        : "missing_data"
      : "draft";

  const warnings = result.findings
    .filter((f) => f.severity === "warning" || f.severity === "critical")
    .map((f) => f.message);

  const src = state.importSource ?? null;

  return {
    exportedAt: new Date().toISOString(),
    isDraft: true,
    requiresHumanReview: true,
    source: src
      ? {
          type: "roof_surface",
          sourceRoofSurfaceId: src.roofSurfaceId,
          buildingId: src.buildingId,
          label: src.label,
          importedAreaM2: src.importedAreaM2,
          importedTiltDeg: src.importedTiltDeg,
          importedAzimuthDeg: src.importedAzimuthDeg,
          geometrySimplified: src.geometrySimplified,
          importedAt: src.importedAt,
        }
      : null,
    polygon: state.polygon,
    scale: hasScale && state.calibration.metersPerPixel
      ? { pixelsPerMeter: 1 / state.calibration.metersPerPixel }
      : null,
    obstacles: state.obstacles.map((obs) => ({
      label: obs.type,
      polygon: [
        { x: obs.x, y: obs.y },
        { x: obs.x + obs.w, y: obs.y },
        { x: obs.x + obs.w, y: obs.y + obs.h },
        { x: obs.x, y: obs.y + obs.h },
      ],
    })),
    result: {
      roofAreaM2: result.projectedAreaM2,
      usableAreaM2: result.usedAreaM2,
      moduleCount: hasPolygon ? result.moduleCount : null,
      estimatedKwp: result.kwp,
      packingDensityPct:
        result.packingDensity !== null && result.packingDensity !== undefined
          ? Math.round(result.packingDensity * 1000) / 10
          : null,
      warnings,
      missingInputs: result.missingData,
      status: resultStatus,
    },
    disclaimer: DESIGN_DISCLAIMER,
  };
}

// Schnelle Flächenvorschau (kein vollständiger Compute-Pass).
export function roofAreaPreview(state: DesignerState): number | null {
  if (state.polygon.length < 3 || !state.calibration.metersPerPixel) return null;
  const mPerPx = state.calibration.metersPerPixel;
  const polyM: Vec2[] = state.polygon.map((p: PixelPt) => ({ x: p.x * mPerPx, y: p.y * mPerPx }));
  return polygonAreaPlanar(polyM);
}
